import React, { PropsWithChildren, ReactElement } from 'react';

import { THeadingProperties } from './heading.types';
import { StyledHeading } from './heading.styled';

/**
 * Use the `Heading` component to communicate the organisation of the content of the page.
 *
 * You should not skip a heading "level" as described in [this accessibility tutorial](https://www.w3.org/WAI/tutorials/page-structure/headings/).
 *
 * The size of the heading is determined by the "size" attribute, not the "level" and sizes vary depending on the size of the screen they're being viewed on.
 *
 * ### Design discussions
 * - Are all the current sizes correct across mobile, tablet and desktop?
 * - How should we offer to add margin top and / or bottom to headings? Should any margin applied be relative to the screen size?
 */
export function Heading({
    level,
    align = 'left',
    inlineSpan = false,
    color = 'indigo',
    size = 'medium',
    weight = 'bold',
    marginTop = 'none',
    marginBottom = 'none',
    children,
    className,
    ...otherProperties
}: PropsWithChildren<THeadingProperties>): ReactElement | null {
    if (!children) {
        return null;
    }

    if (inlineSpan) {
        return (
            <StyledHeading
                as="span"
                align={align}
                size={size}
                textColor={color}
                weight={weight}
                className={className}
                {...otherProperties}
            >
                {children}
            </StyledHeading>
        );
    }

    const validLevels = [1, 2, 3, 4, 5, 6];

    if (validLevels.includes(level as number)) {
        return (
            // @ts-ignore
            <StyledHeading
                // @ts-ignore
                as={`h${level}`}
                align={align}
                size={size}
                textColor={color}
                weight={weight}
                marginTop={marginTop}
                marginBottom={marginBottom}
                className={className}
                {...otherProperties}
            >
                {children}
            </StyledHeading>
        );
    }

    return (
        <StyledHeading
            align={align}
            size={size}
            textColor={color}
            weight={weight}
            marginTop={marginTop}
            marginBottom={marginBottom}
            className={className}
            {...otherProperties}
        >
            {children}
        </StyledHeading>
    );
}
